/**
 * Runtime public configuration (`GET /v1/system/public-config`), read once
 * per page load before sign-in. Today it carries the Cloudflare Turnstile
 * site key, so the key is set on the server (`VELORA_TURNSTILE_SITE_KEY`)
 * instead of being baked into the build. Any failure means "off": a missing
 * config never blocks sign-in on the browser side.
 */
import {
  PublicConfigSchema,
  TURNSTILE_OFF,
  type TurnstilePublic,
} from '@velora/contracts/turnstile';
import { useEffect, useState } from 'react';
import { isDemoMode } from './demo/mode.js';

let pending: Promise<TurnstilePublic> | null = null;

export function loadTurnstileConfig(): Promise<TurnstilePublic> {
  if (isDemoMode()) return Promise.resolve(TURNSTILE_OFF);
  pending ??= globalThis
    .fetch('/v1/system/public-config', {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
    })
    .then(async (r) => (r.ok ? PublicConfigSchema.parse(await r.json()).turnstile : TURNSTILE_OFF))
    .catch(() => {
      pending = null; // try again on the next mount
      return TURNSTILE_OFF;
    });
  return pending;
}

/** The Turnstile config, or `null` while it loads. */
export function useTurnstileConfig(): TurnstilePublic | null {
  const [cfg, setCfg] = useState<TurnstilePublic | null>(null);
  useEffect(() => {
    let live = true;
    void loadTurnstileConfig().then((c) => live && setCfg(c));
    return () => {
      live = false;
    };
  }, []);
  return cfg;
}

/** For tests. */
export function resetTurnstileConfig(): void {
  pending = null;
}
